import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogSurface,
    Text,
    Button,
    Title3,
    Spinner,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import { RootState } from '../../../redux/app/store';
import { CallbackService, CallbackResult } from '../../../libs/services/CallbackService';
import { CallbackPhoneInput } from './CallbackPhoneInput';
import { CallbackConfirmButton } from './CallbackConfirmButton';
import { CallbackSuccess } from './CallbackSuccess';

interface CallbackModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    showDescription?: boolean;
}

type ModalStep = 'acknowledgment' | 'form' | 'confirmation' | 'queueClosed' | 'success';

const useStyles = makeStyles({
    stepContainer: {
        textAlign: 'center',
    },
    title: {
        marginBottom: tokens.spacingVerticalM,
    },
    text: {
        display: 'block',
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
    },
    phoneNumberDisplay: {
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        fontFamily: tokens.fontFamilyBase,
        color: tokens.colorNeutralForeground1,
        backgroundColor: tokens.colorNeutralBackground4,
        width: '100%',
        outline: 'none',
        textAlign: 'center',
        marginTop: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalM,
        cursor: 'not-allowed',
        caretColor: 'transparent',
    },
    buttonGroup: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        justifyContent: 'center',
        marginTop: tokens.spacingVerticalXL,
    },
    errorMessage: {
        display: 'block',
        color: tokens.colorPaletteRedForeground1,
        fontSize: tokens.fontSizeBase200,
        textAlign: 'center',
        padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
        marginTop: tokens.spacingVerticalM,
        backgroundColor: tokens.colorPaletteRedBackground1,
        borderRadius: tokens.borderRadiusMedium,
    },
    spinnerContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
    },
});

export const CallbackModal: React.FC<CallbackModalProps> = ({ isOpen, onOpenChange, showDescription = true }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const auth = useAuth();
    const token = auth.user?.access_token;
    const hostContext = useSelector((state: RootState) => state.hostContext.hostContext);
    const { selectedId } = useSelector((state: RootState) => state.conversations);

    // All state managed in one place
    const [currentStep, setCurrentStep] = useState<ModalStep>('acknowledgment');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isCheckingQueue, setIsCheckingQueue] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const confirmationInputRef = useRef<HTMLInputElement>(null);
    const callbackService = useMemo(() => new CallbackService(token ?? ''), [token]);

    // Reset all state when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentStep('acknowledgment');
            setPhoneNumber('');
            setIsCheckingQueue(false);
            setIsSubmitting(false);
            setErrorMessage(null);
        }
    }, [isOpen]);

    // Auto-focus the confirmation input when reaching the confirmation step
    useEffect(() => {
        if (currentStep === 'confirmation' && confirmationInputRef.current) {
            confirmationInputRef.current.focus();
        }
    }, [currentStep]);

    const handleAcknowledge = async () => {
        setIsCheckingQueue(true);
        setErrorMessage(null);

        try {
            const queueStatus = await callbackService.getQueueStatusAsync(hostContext);
            setCurrentStep(queueStatus.status === 'Closed' ? 'queueClosed' : 'form');
        } catch (error) {
            console.error('Error checking queue status:', error);
            // Fall through to form even on error
            setCurrentStep('form');
        } finally {
            setIsCheckingQueue(false);
        }
    };

    const handlePhoneSubmit = () => {
        // Move to confirmation step
        setErrorMessage(null);
        setCurrentStep('confirmation');
    };

    const handleConfirmSubmit = async () => {
        if (isSubmitting || !phoneNumber.trim()) return;

        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const result: CallbackResult = await callbackService.requestCallbackAsync(
                phoneNumber,
                selectedId,
                hostContext,
            );

            if (result.success) {
                setCurrentStep('success');
            } else {
                // Use the specific error message from the service if available
                const message = result.errorMessage ? t(result.errorMessage) : t('callback.errorMessage');
                setErrorMessage(message);
            }
        } catch (error) {
            console.error('Error submitting callback request:', error);
            setErrorMessage(t('callback.errorMessage'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBack = () => {
        setErrorMessage(null);
        setCurrentStep('acknowledgment');
    };

    const handleBackToForm = () => {
        setErrorMessage(null);
        setCurrentStep('form');
    };

    const handleClose = () => {
        onOpenChange(false);
        // Reset to initial state
        setCurrentStep('acknowledgment');
        setPhoneNumber('');
        setErrorMessage(null);
    };

    return (
        <Dialog
            modalType="modal"
            open={isOpen}
            onOpenChange={(_, data) => {
                if (!data.open) {
                    handleClose();
                }
            }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogContent>
                        {currentStep === 'acknowledgment' && (
                            <div className={classes.stepContainer}>
                                <Title3 className={classes.title}>{t('callback.acknowledgmentTitle')}</Title3>
                                <Text className={classes.text}>{t('callback.acknowledgmentMessage')}</Text>
                                <div className={classes.buttonGroup}>
                                    <Button appearance="secondary" onClick={handleClose} disabled={isCheckingQueue}>
                                        {t('callback.cancelButton')}
                                    </Button>
                                    <Button
                                        appearance="primary"
                                        onClick={() => {
                                            void handleAcknowledge();
                                        }}
                                        disabled={isCheckingQueue}
                                    >
                                        {isCheckingQueue ? (
                                            <div className={classes.spinnerContainer}>
                                                <Spinner size="tiny" />
                                                {t('callback.acknowledgeButton')}
                                            </div>
                                        ) : (
                                            t('callback.acknowledgeButton')
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 'queueClosed' && (
                            <div className={classes.stepContainer}>
                                <Text className={classes.text}>{t('callback.queueClosedMessage')}</Text>
                                <div className={classes.buttonGroup}>
                                    <Button appearance="primary" onClick={handleClose}>
                                        {t('callback.closeButton')}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {currentStep === 'form' && (
                            <>
                                {showDescription && (
                                    <Text className={classes.text}>{t('callback.modalDescription')}</Text>
                                )}
                                <CallbackPhoneInput
                                    value={phoneNumber}
                                    onChange={setPhoneNumber}
                                    onSubmit={handlePhoneSubmit}
                                    onBack={handleBack}
                                    disabled={isSubmitting}
                                    errorMessage={errorMessage}
                                />
                            </>
                        )}

                        {currentStep === 'confirmation' && (
                            <form
                                className={classes.stepContainer}
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    void handleConfirmSubmit();
                                }}
                            >
                                <Title3 className={classes.title}>{t('callback.confirmationTitle')}</Title3>
                                <Text className={classes.text}>{t('callback.confirmationMessage')}</Text>
                                <input
                                    ref={confirmationInputRef}
                                    type="tel"
                                    value={phoneNumber}
                                    readOnly
                                    className={classes.phoneNumberDisplay}
                                    aria-label={t('callback.phoneNumberPlaceholder')}
                                />
                                {errorMessage && <Text className={classes.errorMessage}>{errorMessage}</Text>}
                                <div className={classes.buttonGroup}>
                                    <Button appearance="secondary" onClick={handleBackToForm} type="button">
                                        {t('callback.backButton')}
                                    </Button>
                                    <CallbackConfirmButton isLoading={isSubmitting} />
                                </div>
                            </form>
                        )}

                        {currentStep === 'success' && <CallbackSuccess onClose={handleClose} />}
                    </DialogContent>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
